<script lang="ts">
    import Fa from 'svelte-fa'
    import Popover from './popover.svelte'
    import { faTimes } from '@fortawesome/free-solid-svg-icons'
    import { onDestroy, onMount } from 'svelte'
    import Paywall from './paywall.svelte'
    import dayjs from 'dayjs'
    import { Link, navigateTo } from 'yrv'
    import type { Channel } from '../../modules/brokerApi/channels/channel'

    let showAppAlert = false
    let showPlanAlert = false
    // Cobrança da assinatura emitida e ainda pagável. Aparece no lugar do alerta vermelho de erro,
    // que não é verdade enquanto o documento está em aberto dentro do prazo.
    let showChargeAvailable = false
    let showTrialAlert = false
    // Assinatura cancelada no payments-api: a users-api calcula planAccessEndsAt (já considerando o
    // período pago) e cancela a empresa quando a data chega. Aqui só se conta os dias até ela, para o
    // usuário salvar os dados antes.
    let showCancelAlert = false
    let cancelAccessInDays = 0

    let showPlanInfo = false
    let showTrialInfo = false
    let showAppInfo = false
    let planExpiresIn = 365

    onMount(async () => {
        setTimeout(evaluateAlerts, 50)
    })

    async function evaluateAlerts() {
        Promise.all([evaluatePlan(), evaluateOutbox(), evaluateNotifications()])
    }

    onDestroy(() => {
        if (timeout) clearTimeout(timeout)
    })

    function openCheckout() {
        console.log('checkout')
        alert('Ops. Algo deu errado na ativação automática!\nEntre em contato com nosso consultor pelo WhatsApp: (16)9.8855-4705')
    }

    function openSales() {
        console.log('sales')
        location.href = 'https://wa.me/5516988554705?text=Oi! Testei o Zandu e quero saber sobre a assinatura.'
    }

    function openNotifications() {
        console.log('notifications')
        showNotifAlert = false
        navigateTo('/personalize/notifications')
    }

    let timeout = null
    async function evaluatePlan() {
        if (!window.store.company?.companyId) {
            setTimeout(evaluateAlerts, 5000)
            return
        }
        timeout = setTimeout(evaluateAlerts, 1000 * 60 * 60 * 2)
        try {
            await window.api.users.companies.setCurrent(window.store.company.companyId)
            showPlanAlert =
                window.store.company.planStatus === 'blocked' ||
                window.store.company.planStatus === 'blocked_trial' ||
                window.store.company.planStatus === 'renew' ||
                window.store.company.planStatus === 'overdue'

            // reavaliado a cada volta: pago o boleto ou o pix, o aviso sai sozinho
            showChargeAvailable = false

            if (window.store.company.planMethod === 'bankslip') {
                let expiresAt = new Date(window.store.company.planExpiresAt)
                if (window.store.company.planPastDueDays == 0 && new Date().getHours() >= 11) {
                    if (expiresAt.getDate() == new Date().getDate()) {
                        showPlanAlert = false
                        showChargeAvailable = true
                    }
                }
                const diffHours = (new Date(window.store.company.planExpiresAt).getTime() - Date.now()) / 36e5
                const waitCompensation = window.store.company.planPastDueDays <= 3
                if (diffHours < 66 && waitCompensation) {
                    showPlanAlert = false
                    showChargeAvailable = true
                }
            }

            // No pix não há compensação para esperar, e por isso não há janela de horas como a do
            // boleto: ou existe um QR Code vivo e pagável, ou não existe. A pergunta então é factual,
            // feita na própria assinatura — e não deduzida da data de vencimento.
            if (window.store.company.planMethod === 'pix' && (await temPixEmAberto())) {
                showChargeAvailable = true
                // assinatura bloqueada já passou do prazo: ali o aviso certo é o do bloqueio, com o
                // paywall. O caminho para pagar continua no botão "Ver assinatura" daquele alerta.
                const bloqueada = window.store.company.planStatus === 'blocked' || window.store.company.planStatus === 'blocked_trial'
                if (!bloqueada) showPlanAlert = false
            }

            if (window.store.company.planForceChargeAlert) showPlanAlert = true
            showTrialAlert = window.store.company.planStatus === 'trial'

            // Arredonda pra cima: o banner nunca deve prometer menos dias do que o usuário ainda tem.
            // Zerado ou negativo não mostra, a empresa já vai ser cancelada na próxima varredura.
            if (window.store.company.planAccessEndsAt) {
                const accessEndsAt = new Date(window.store.company.planAccessEndsAt).getTime()
                cancelAccessInDays = Math.ceil((accessEndsAt - Date.now()) / 864e5)
                showCancelAlert = cancelAccessInDays > 0
            }
            planExpiresIn = dayjs(window.store.company.planExpiresAt).diff(new Date(), 'day') // data de vencimento conforme último pagamento

            //}
        } catch (error) {}
    }

    /**
     * A assinatura tem um pix emitido que ainda dá para pagar?
     *
     * Lê a própria assinatura, como faz a tela de assinatura: o QR vive no invoice, e é o dueDate
     * dele que diz até quando o código vale (no pix esse campo é o instante em que o QR expira, não
     * o vencimento da mensalidade). Parcela já paga ou cancelada fica de fora — o código dela pode
     * até existir, mas não há o que pagar.
     */
    async function temPixEmAberto(): Promise<boolean> {
        const purchaseId = window.store.company?.planPurchaseId
        if (!purchaseId) return false
        try {
            const purchase = await window.api.payments.purchases.getOnePublic(purchaseId, true)
            const agora = Date.now()
            for (const charge of purchase?.charges || []) {
                // só as que ainda esperam pagamento (a faixa 100-199 do status)
                if (charge.status < 100 || charge.status >= 200) continue
                for (const invoice of charge.invoices || []) {
                    // o meio tem de ser pix: boletos antigos do pagar.me trazem a URL do QR do
                    // boleto no campo do pix, e passariam por pix em aberto
                    if (invoice.paymentMethod !== 'pix' || !invoice.pixEMV) continue
                    if (invoice.dueDate && new Date(invoice.dueDate).getTime() < agora) continue
                    return true
                }
            }
        } catch (error) {}
        return false
    }

    async function evaluateOutbox() {
        try {
            const channels: Channel[] = await window.api.brokers.channels.getAll({ updateStatus: false })
            for (const channel of channels) {
                if (channel.s10 !== null) {
                    if (channel.s10 < 90) {
                        showAppAlert = true
                        return
                    }
                }
            }
            showAppAlert = false
            //}
        } catch (error) {}
    }

    let showNotifAlert = false
    async function evaluateNotifications() {
        // só aparece o alerta no iphone se estiver instalado.
        return // todo:quando a notificação fizer sentido, remover o return
        if (window.api.utils.utils.isIOS() && !window.api.utils.utils.isInstalled) return
        try {
            if (Notification?.permission === 'default') {
                showNotifAlert = true
            }
        } catch (error) {}
    }
</script>

{#if showTrialInfo}
    <Popover on:close={() => (showTrialInfo = false)} width="890px" height="200px">
        <div class="config-box m-4 text-center">
            <p class="subtitle">Ative sua Assinatura</p>
            <p>Ative agora sua assinatura e desfrute de todos os recursos do Zandu.</p>
            <!-- // se está no início dos testes não vê o preço. só consegue falar com o consultor. caso contrário cai no checkout. -->
            <button class="btn btn-primary" on:click={openSales}>Solicitar ativação</button>
            <!-- {#if window.plan.expiresIn > 1}
                <button class="btn btn-primary" on:click={openSales}>Solicitar ativação</button>
            {:else}
                <button class="btn btn-primary" on:click={openCheckout}>Ativar assinatura</button>
            {/if} -->
        </div>
    </Popover>
{/if}

{#if showAppInfo}
    <Popover on:close={() => (showAppInfo = false)} width="890px" height="200px">
        <div class="config-box m-4">
            <p class="subtitle">Caixa de saída com problemas</p>
            <p>As automações do Zandu enviaram mensagens que estão paradas na caixa de saída.</p>
            <p>Para usuários do Zandu Comunicação:</p>
            <ul>
                <li>Verifique se o programa de mensagens está aberto no computador</li>
                <li>Verifique se o WhatsApp está conectado</li>
                <li>Caso esteja aberto e conectado feche e reabra o programa de mensagens</li>
                <li>Caso continue com a mensagem <b>reinicie seu computador e reabra o aplicativo</b></li>
            </ul>
            <p>Para outros provedores:</p>
            <ul>
                <li>Verifique se o serviço está em funcionamento</li>
                <li>Se necessário, entre em contato com o suporte</li>
            </ul>
            <p>Após esses procedimentos as mensagens da caixa de saída deverão ser processadas nos minutos seguintes.</p>
        </div>
    </Popover>
{/if}

{#if showPlanInfo}
    <Popover on:close={() => (showPlanInfo = false)} width="890px">
        <div class="config-box m-4">
            <p class="subtitle">Não foi possível receber seu último pagamento.</p>
            <p>Isso pode ocorrer por vários motivos:</p>
            <ul>
                <li>Seu banco negou o pagamento por um bloqueio de segurança;</li>
                <li>Seu cartão de crédito está bloqueado ou vencido;</li>
                <li>Seu cartão de crédito está sem limite.</li>
            </ul>
            <p>
                Em caso de bloqueio de segurança:<br />
                <span class="text-muted">
                    Entre em contato com seu banco e informe o valor da parcela que foi negada e a data. Informe que tentou fazer o pagamento mas foi negado e
                    que você gostaria de autorizar este pagamento.<br />
                    Após o banco informar que foi feita a liberação basta aguardar que tentaremos cobrar novamente em seu cartão.
                </span>
            </p>
            <p>
                Em caso de cartão vencido ou bloqueado:<br />
                <span class="text-muted"> Entre em contato com o suporte e solicite um link para atualizar os dados de seu cartão. </span>
            </p>
            <p>
                Em caso de falta de limite:<br />
                <span class="text-muted">
                    Tentaremos cobrar novamente outra vez em seu cartão muito em breve. Caso haja limite a cobrança será feita automaticamente.
                </span>
            </p>
            <p>
                Dúvidas?<br />
                Entre em contato com o financeiro: financeiro@zandu.com.br
            </p>
            <div class="alert alert-warning">
                Importante: em caso de bloqueio por falha no pagamento o desbloqueio não poderá ser feito pelo suporte.<br />
                Neste caso entre em contato com o financeiro pelo e-mail financeiro@zandu.com.br
            </div>
        </div>
    </Popover>
{/if}

{#if window.store.alert}
    <div class="login-alert h-print">
        Você está logado na empresa <b>{window.store.company?.companyName}</b> através do usuário
        <b>{window.store.user?.userName || '-'} / {window.api.utils.utils.phoneFriendly(window.store.user?.phone) || '-'} / {window.store.user?.email || '-'}</b
        >
    </div>
{/if}

{#if showTrialAlert}
    <div class="trial-alert h-print">
        <div>
            {#if planExpiresIn > 1}
                Versão de demonstração. Você possui hoje e mais <b>{planExpiresIn}</b> dias para experimentar o Zandu.
            {:else if planExpiresIn == 1}
                Versão de demonstração. Você possui hoje e mais <b>1</b> dia para experimentar o Zandu.
            {:else}
                Hoje é seu último dia para testar o Zandu.
            {/if}
            <button class="btn" on:click={() => (showTrialInfo = true)}>Quero assinar</button>
        </div>
        <button class="btn" on:click={() => (showTrialAlert = false)}>
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}

{#if showCancelAlert}
    <!-- fica atrás do Paywall de propósito: empresa já bloqueada continua bloqueada, o aviso não libera nada -->
    <div class="plan-alert h-print">
        <div>
            Sua assinatura foi cancelada. {#if cancelAccessInDays > 1}Em <b>{cancelAccessInDays}</b> dias{:else}Amanhã{/if}
            você não terá mais acesso. É importante salvar as suas informações.
            <Link class="btn btn-sm btn-secondary" href="/resources/subscription">Ver assinatura</Link>
        </div>
        <button
            class="btn"
            on:click={() => {
                showCancelAlert = false
            }}
        >
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}

{#if showChargeAvailable}
    <!-- vale para boleto e pix: o meio aparece na tela de assinatura, junto do botão de pagar -->
    <div class="trial-alert h-print">
        <div>
            A cobrança da sua assinatura já está disponível.
            <Link class="btn btn-sm btn-secondary" href="/resources/subscription">Ver assinatura</Link>
        </div>
        <button
            class="btn"
            on:click={() => {
                showChargeAvailable = false
            }}
        >
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}

{#if showPlanAlert}
    <Paywall />
    <div class="plan-alert h-print">
        <div>
            {#if window?.store?.company?.planMethod === 'bankslip'}
                Erro na assinatura (lembre-se: boletos levam até 2 dias úteis para compensação)
            {:else}
                Ocorreu um erro com sua assinatura
            {/if}
            <Link class="btn btn-sm btn-secondary" href="/resources/subscription">Ver assinatura</Link>
            <!-- <button class="btn" on:click={() => (showPlanInfo = true)}>Mais informações</button> -->
        </div>
        <button
            class="btn"
            on:click={() => {
                showPlanAlert = false
            }}
        >
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}

{#if showAppAlert}
    <div class="outbox-alert h-print">
        <div>
            Você possui mensagens <b>não enviadas</b> na caixa de saída. Verifique o aplicativo de mensagens.
            <button class="btn" on:click={() => (showAppInfo = true)}>Mais informações</button>
        </div>
        <button class="btn" on:click={() => (showAppAlert = false)}>
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}

{#if showNotifAlert}
    <div class="outbox-alert h-print">
        <div>
            Você ainda não ativou as notificações para este dispositivo.
            <button class="btn" on:click={openNotifications}>Ativar notificações</button>
        </div>
        <button class="btn" on:click={() => (showNotifAlert = false)}>
            <Fa icon={faTimes} />
        </button>
    </div>
{/if}
